import { KIT_MESSAGES, kitText, type KitMessageKey } from '@affdice/portal-kit';

import { t, type AmsMessageKey } from '../../messages.ts';

/**
 * Platform → Roles & permissions (owner decision 2, Rule Book §3.12).
 *
 * The pure logic of the screen, kept out of the .tsx so a test can read it
 * without a DOM. Its words are in messages/platform.ts under `plat.roles.`.
 */

export interface CapabilityRow {
  readonly code: string;
  readonly description: string;
}

export interface RoleRow {
  readonly id: string;
  readonly code: string;
  /** The super admin: every capability, always, and nothing to toggle. */
  readonly locked: boolean;
  readonly capabilities: readonly string[];
}

export interface PlatformUserRow {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly roles: readonly string[];
  readonly extra: readonly string[];
}

export interface CapabilityMatrix {
  readonly capabilities: readonly CapabilityRow[];
  readonly roles: readonly RoleRow[];
  readonly users: readonly PlatformUserRow[];
  readonly mine: readonly string[];
  readonly can_manage: boolean;
}

export interface CapabilityHistoryRow {
  readonly id: string;
  readonly action: 'platform.capabilities.role' | 'platform.capabilities.user' | string;
  readonly entity_id: string | null;
  readonly before_value: { role?: string; user?: string; capabilities?: string[] } | null;
  readonly after_value: { role?: string; user?: string; capabilities?: string[] } | null;
  readonly reason: string | null;
  readonly actor: string | null;
  readonly at: string;
}

/** Each capability's words, keyed literally so the catalogue test sees them used. */
const CAPABILITY_KEYS: Readonly<Record<string, AmsMessageKey>> = {
  'operators.view': 'plat.roles.cap.operators.view',
  'brands.view': 'plat.roles.cap.brands.view',
  'affiliates.view': 'plat.roles.cap.affiliates.view',
  'entities.edit': 'plat.roles.cap.entities.edit',
  'entities.suspend': 'plat.roles.cap.entities.suspend',
  'entities.delete': 'plat.roles.cap.entities.delete',
  'entities.restore': 'plat.roles.cap.entities.restore',
  'entities.purge': 'plat.roles.cap.entities.purge',
  'operator_team.manage': 'plat.roles.cap.operator_team.manage',
  'credentials.reset': 'plat.roles.cap.credentials.reset',
  'login_as.operator_user': 'plat.roles.cap.login_as.operator_user',
  'login_as.brand_user': 'plat.roles.cap.login_as.brand_user',
  'login_as.affiliate': 'plat.roles.cap.login_as.affiliate',
  'platform_users.manage': 'plat.roles.cap.platform_users.manage',
  'platform_permissions.manage': 'plat.roles.cap.platform_permissions.manage',
  'platform.configure': 'plat.roles.cap.platform.configure',
  'audit.view': 'plat.roles.cap.audit.view',
};

/** A capability in words; an unknown code (a newer server) shows the server's own description. */
export function capabilityLabel(code: string, fallback?: string): string {
  const key = CAPABILITY_KEYS[code];
  return key ? t(key) : (fallback ?? code);
}

/** A platform role's name, as the Users screen already shows it. */
export function roleLabel(code: string): string {
  const key = `role.${code}`;
  return key in KIT_MESSAGES ? kitText(key as KitMessageKey) : code;
}

type GroupKey =
  | 'plat.roles.group.view' | 'plat.roles.group.change' | 'plat.roles.group.accounts'
  | 'plat.roles.group.loginAs' | 'plat.roles.group.platform';

function groupOf(code: string): GroupKey {
  if (code.endsWith('.view')) return 'plat.roles.group.view';
  if (code.startsWith('entities.')) return 'plat.roles.group.change';
  if (code.startsWith('login_as.')) return 'plat.roles.group.loginAs';
  if (code === 'operator_team.manage' || code === 'credentials.reset') return 'plat.roles.group.accounts';
  return 'plat.roles.group.platform';
}

/** The catalogue in its server order, cut into the runs the table heads. */
export function groupCapabilities(
  caps: readonly CapabilityRow[],
): { group: GroupKey; items: CapabilityRow[] }[] {
  const out: { group: GroupKey; items: CapabilityRow[] }[] = [];
  for (const c of caps) {
    const g = groupOf(c.code);
    const last = out[out.length - 1];
    if (last && last.group === g) last.items.push(c);
    else out.push({ group: g, items: [c] });
  }
  return out;
}

/**
 * The roles the matrix draws a column for. The super admin's role is left
 * out (owner 2026-10-07): it always holds every permission and nothing here
 * can change that, so a column of ticked, locked boxes said nothing a short
 * note above the table does not.
 */
export function matrixRoles<R extends Pick<RoleRow, 'locked'>>(roles: readonly R[]): R[] {
  return roles.filter((r) => !r.locked);
}

/** What a draft would give and take away, against what is saved. */
export function draftDiff(
  saved: readonly string[],
  draft: ReadonlySet<string>,
): { added: string[]; removed: string[] } {
  const before = new Set(saved);
  return {
    added: [...draft].filter((c) => !before.has(c)),
    removed: saved.filter((c) => !draft.has(c)),
  };
}

/** One history row as "Admin: gives X; takes away Y". */
export function historyLine(row: CapabilityHistoryRow, capabilityNames: (code: string) => string): string {
  const subject = row.action === 'platform.capabilities.role'
    ? roleLabel(row.after_value?.role ?? row.before_value?.role ?? '')
    : (row.after_value?.user ?? row.before_value?.user ?? '');
  const { added, removed } = draftDiff(
    row.before_value?.capabilities ?? [],
    new Set(row.after_value?.capabilities ?? []),
  );
  const parts: string[] = [];
  if (added.length) parts.push(`${t('plat.roles.grant')} ${added.map(capabilityNames).join(', ')}`);
  if (removed.length) parts.push(`${t('plat.roles.revoke')} ${removed.map(capabilityNames).join(', ')}`);
  return `${subject}: ${parts.length ? parts.join('; ') : t('plat.roles.nothingChanged')}`;
}
